import { type ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, cn } from '@kognitos/lattice';

import { useIsOverflowing } from '@/shared/hooks/useIsOverflowing';

const LINE_CLAMP = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
} as const;

/** Props for {@link ClampedText}. */
export interface IClampedTextProps {
  children: ReactNode;
  /** Lines shown while collapsed. */
  lines?: keyof typeof LINE_CLAMP;
  /** What the content depends on, usually its text: a change re-measures the clamp and collapses it again. */
  contentKey: unknown;
  className?: string;
}

/**
 * Prose clamped to a few lines, with a "Show more"/"Show less" toggle that
 * expands it in place. The clamp is CSS-only, so the full content stays in the
 * DOM, and the toggle is offered only when the clamp actually cuts text off.
 * For a one-line table cell use `ExpandableCell` instead.
 *
 * @param props - The content, the collapsed line count and the content's key
 * @returns The clamped content and, when clipped, its toggle
 */
export function ClampedText({
  children,
  lines = 3,
  contentKey,
  className,
}: IClampedTextProps) {
  const { t } = useTranslation('common');
  const [expanded, setExpanded] = useState(false);
  const [shownKey, setShownKey] = useState(contentKey);
  const clampRef = useRef<HTMLDivElement>(null);
  // A clamped box keeps its height when its content is swapped, so the ResizeObserver alone never notices.
  const isClipped = useIsOverflowing(clampRef, 'vertical', contentKey);

  if (shownKey !== contentKey) {
    setShownKey(contentKey);
    setExpanded(false);
  }

  // Expanded content no longer overflows, so keep the toggle as the way back.
  const canToggle = expanded || isClipped;

  return (
    <Flex
      direction="column"
      gap={1}
      align="start"
      className={cn('w-full min-w-0', className)}
    >
      {/* A plain div: line-clamp sets its own display, which Flex would override */}
      <div
        ref={clampRef}
        className={cn('w-full break-words', !expanded && LINE_CLAMP[lines])}
      >
        {children}
      </div>
      {canToggle && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          {t(expanded ? 'common.action.showLess' : 'common.action.showMore')}
        </Button>
      )}
    </Flex>
  );
}
